// src/components/pages/MisCursos.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AlertDialog from "../Components/Shared/AlertDialog";
import {
  FiBookOpen,
  FiClock,
  FiCheckCircle,
  FiThumbsUp,
  FiSlash,
  FiTrendingUp,
  FiSearch,
} from "react-icons/fi";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { ENDPOINTS as SEGURIDAD_ENDPOINTS } from "../../Seguridad/Utils/Endpoints";
import { API_BASE } from "../../../api";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";

const MisCursos = () => {
  const navigate = useNavigate();

  const [cursos, setCursos] = useState([]);
  const [tab, setTab] = useState("todo");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSkill, setSelectedSkill] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [niveles, setNiveles] = useState([]);
  const [tiposHabilidad, setTiposHabilidad] = useState([]);

  const [resumen, setResumen] = useState({
    totalMatriculados: 0,
    enCurso: 0,
    aprobados: 0,
    reprobados: 0,
    promedioGeneral: 0
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

  useEffect(() => {
    const loadData = async () => {
      const idUsuarioSesion = getSessionUserId();
      if (!idUsuarioSesion) {
        setCursos([]);
        return;
      }

      setLoading(true);
      try {
        // console.log('[MisCursos] Cargando datos consolidados para idUsuario:', idUsuarioSesion);
        const res = await axios.get(LMS_ENDPOINTS.misCursos(idUsuarioSesion));
        const rawData = res.data || {};

        // 1. Normalizar Cursos
        const rawCursos = rawData.Cursos || rawData.cursos || [];
        const normalizedCursos = rawCursos.map(c => {
          const certificadoGenerado = c.CertificadoGenerado ?? c.certificadoGenerado ?? false;
          let estado = (c.Estado ?? c.estado ?? 'en-curso').toLowerCase();

          // Si el certificado ya fue generado, el curso debe mostrarse como finalizado/aprobado
          // indistintamente de lo que diga el campo estado (por si hay retraso en sync)
          if (certificadoGenerado && estado === 'en-curso') {
            estado = 'finalizado';
          }

          return {
            idEstudiantesCursos: c.IdEstudiantesCursos ?? c.idEstudiantesCursos,
            idCurso: c.IdCurso ?? c.idCurso,
            nombre: c.Nombre ?? c.nombre ?? '',
            imagenRuta: c.ImagenRuta ?? c.imagenRuta ?? '',
            calificacionFinal: c.CalificacionFinal ?? c.calificacionFinal,
            resultado: c.Resultado ?? c.resultado ?? '—',
            ultimoAcceso: c.UltimoAcceso ?? c.ultimoAcceso,
            estado: estado,
            idNivel: c.IdNivel ?? c.idNivel,
            nivelNombre: c.NivelNombre ?? c.nivelNombre ?? '',
            idTipoHabilidad: c.IdTipoHabilidad ?? c.idTipoHabilidad,
            tipoHabilidadNombre: c.TipoHabilidadNombre ?? c.tipoHabilidadNombre ?? '',
            instructorNombre: c.InstructorNombre ?? c.instructorNombre ?? '',
            duracion: c.Duracion ?? c.duracion,
            certificadoGenerado: certificadoGenerado,
            fechaTermino: c.FechaTermino ?? c.fechaTermino ?? c.FechTermino ?? c.fechTermino
          };
        });
        setCursos(normalizedCursos);

        // 2. Calcular Resumen consolidado para asegurar consistencia con los datos mostrados
        const calculatedResumen = {
          totalMatriculados: normalizedCursos.length,
          enCurso: normalizedCursos.filter(c => c.estado === 'en-curso').length,
          aprobados: normalizedCursos.filter(c => c.estado === 'aprobado').length,
          reprobados: normalizedCursos.filter(c => c.estado === 'reprobado' || c.estado === 'suspendido').length,
          promedioGeneral: 0
        };

        const ratedCursos = normalizedCursos.filter(c => c.calificacionFinal !== null && c.calificacionFinal !== undefined);
        if (ratedCursos.length > 0) {
          const sum = ratedCursos.reduce((acc, c) => acc + Number(c.calificacionFinal), 0);
          calculatedResumen.promedioGeneral = sum / ratedCursos.length;
        } else {
          calculatedResumen.promedioGeneral = null;
        }
        setResumen(calculatedResumen);

        // 3. Normalizar Niveles y Habilidades
        setNiveles((rawData.Niveles || rawData.niveles || []).map(n => ({
          idNivel: n.IdNivel ?? n.idNivel,
          nombre: n.Nombre ?? n.nombre
        })));
        setTiposHabilidad((rawData.TiposHabilidad || rawData.tiposHabilidad || []).map(th => ({
          idTipoHabilidad: th.IdTipoHabilidad ?? th.idTipoHabilidad,
          nombre: th.Nombre ?? th.nombre
        })));
      } catch (error) {
        console.error("Error al cargar mis cursos consolidado:", error);
        setCursos([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const renderStatusPill = (estado) => {
    const commonStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
      padding: '6px 14px',
      borderRadius: '999px',
      fontSize: '0.8rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    };

    switch (estado) {
      case "en-curso":
        return (
          <span style={{ ...commonStyle, backgroundColor: '#e0f2fe', color: '#0369a1' }}>
            <FiClock /> En curso
          </span>
        );
      case "finalizado":
      case "aprobado":
        return (
          <span style={{ ...commonStyle, backgroundColor: '#dcfce7', color: '#15803d' }}>
            <FiCheckCircle /> {estado === "aprobado" ? "Aprobado" : "Finalizado"}
          </span>
        );
      case "suspendido":
      case "reprobado":
        return (
          <span style={{ ...commonStyle, backgroundColor: '#fee2e2', color: '#991b1b' }}>
            <FiSlash /> {estado === "reprobado" ? "Reprobado" : "Suspendido"}
          </span>
        );
      default:
        return <span className="status-pill">{estado}</span>;
    }
  };

  const filtrados = (cursos || [])
    .filter((c) => {
      // 1. Filtro de Tab
      if (tab !== "todo") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deadlineDate = c.fechaTermino ? new Date(c.fechaTermino) : null;
        if (deadlineDate) deadlineDate.setHours(0, 0, 0, 0);
        const isExpired = deadlineDate && today > deadlineDate;

        if (tab === "expirado") {
          if (!isExpired) return false;
        } else if (tab === "reprobado") {
          if (c.estado !== "reprobado" && c.estado !== "suspendido") return false;
        } else {
          // Si pedimos otro tab, no debemos mostrar los expirados
          if (isExpired) return false;
          if (c.estado !== tab) return false;
        }
      }

      // 2. Filtro de Búsqueda
      if (searchTerm && !(c.nombre || "").toLowerCase().includes(searchTerm.toLowerCase())) return false;

      // 3. Filtro de Habilidad
      if (selectedSkill && String(c.idTipoHabilidad) !== String(selectedSkill)) return false;

      // 4. Filtro de Nivel
      if (selectedLevel && String(c.idNivel) !== String(selectedLevel)) return false;

      return true;
    })
    .sort((a, b) => {
      // Cálculo de prioridad para ordenamiento
      const getPriority = (curso) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deadlineDate = curso.fechaTermino ? new Date(curso.fechaTermino) : null;
        if (deadlineDate) deadlineDate.setHours(0, 0, 0, 0);

        const isExpired = deadlineDate && today > deadlineDate;
        const isAboutToExpire = curso.estado === 'en-curso' && deadlineDate && today <= deadlineDate;

        if (isExpired) return 5; // 5. Expirados (Final final)
        if (isAboutToExpire) return 1; // 1. Por terminar (Amarillo)
        if (curso.estado === 'en-curso') return 2; // 2. En curso sin fecha o normal (Azul)
        if (curso.estado === 'finalizado' || curso.estado === 'aprobado') return 3; // 3. Aprobados (Verde)
        return 4; // 4. Reprobados/Suspendidos (Rojo)
      };

      return getPriority(a) - getPriority(b);
    });

  const handleOpenCurso = (curso) => {
    if (!curso.idCurso) return;
    navigate(`/menu/lms/curso-estudiante/${curso.idCurso}`);
  };

  const handleSolicitarExtensionClick = (curso) => {
    setAlertConfig({
      title: 'Solicitar Extensión',
      message: 'Este curso ha expirado. ¿Deseas enviar una solicitud al administrador para que extienda tu fecha límite?',
      type: 'warning',
      buttonText: 'Sí, solicitar',
      cancelText: 'Cancelar',
      onCancel: () => setAlertOpen(false),
      onClose: () => confirmarSolicitud(curso)
    });
    setAlertOpen(true);
  };

  const confirmarSolicitud = async (curso) => {
    setAlertOpen(false); // Cerramos el dialogo de pregunta
    try {
      const payload = {
        IdPeticionFechaExtendida: 0,
        IdEstudianteCurso: curso.idEstudiantesCursos,
        FechaAnterior: curso.fechaTermino,
        FechaActual: null,
        Atendido: false,
        Aceptado: false,
        IdUsuarioAtendedor: null,
        Activo: true
      };

      await axios.post(LMS_ENDPOINTS.peticionFechaExtendida, payload);

      // Mostrar modal de éxito
      setTimeout(() => {
        setAlertConfig({
          title: '¡Solicitud enviada!',
          message: 'Se ha enviado tu solicitud. Te notificaremos cuando haya sido revisada.',
          type: 'success',
          buttonText: 'Aceptar',
          onCancel: null,
          onClose: () => setAlertOpen(false)
        });
        setAlertOpen(true);
      }, 200);

    } catch (error) {
      console.error('Error enviando petición:', error);
      setTimeout(() => {
        setAlertConfig({
          title: 'Error',
          message: 'Ocurrió un error al enviar la solicitud. Por favor intenta de nuevo más tarde.',
          type: 'error',
          buttonText: 'Aceptar',
          onCancel: null,
          onClose: () => setAlertOpen(false)
        });
        setAlertOpen(true);
      }, 200);
    }
  };

  return (
    <div className="panel-sigma-main">
      <div className="profile-header-title">
        <h1>Mis cursos</h1>
      </div>

      {/* Estadísticas estilo AdministrarCursos */}
      <div className="stats-grid stats-grid-wrap">
        <div className="stat-card">
          <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)' }}>
            <FiBookOpen />
          </div>
          <div>
            <p className="stat-label">Cursos matriculados</p>
            <p className="stat-value">{resumen.totalMatriculados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)' }}>
            <FiThumbsUp />
          </div>
          <div>
            <p className="stat-label">Aprobados</p>
            <p className="stat-value">{resumen.aprobados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)' }}>
            <FiClock />
          </div>
          <div>
            <p className="stat-label">En curso</p>
            <p className="stat-value">{resumen.enCurso}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)' }}>
            <FiSlash />
          </div>
          <div>
            <p className="stat-label">Reprobados</p>
            <p className="stat-value">{resumen.reprobados}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)' }}>
            <FiTrendingUp />
          </div>
          <div>
            <p className="stat-label">Promedio general</p>
            <p className="stat-value">
              {resumen.promedioGeneral !== null && resumen.promedioGeneral !== undefined
                ? `${Math.round(resumen.promedioGeneral)}%`
                : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* UI de Toggle, Búsqueda y Filtros - Estilo "Referencia/Comentario" */}
      <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>

        {/* 1. Header (Toggle Tabs) */}
        <div style={{
          display: 'flex',
          width: '100%',
          backgroundColor: '#f1f5f9',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
          padding: '0.4rem',
          gap: '0.5rem',
          border: '1px solid #e2e8f0',
          borderBottom: 'none'
        }}>
          {['todo', 'aprobado', 'en-curso', 'reprobado', 'expirado'].map((option) => (
            <div
              key={option}
              onClick={() => setTab(option)}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '0.4rem 0',
                fontSize: '0.9rem',
                fontWeight: tab === option ? 700 : 500,
                color: tab === option ? 'var(--color-primary-dark)' : '#64748b',
                backgroundColor: tab === option ? '#ffffff' : 'transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                userSelect: 'none',
                textTransform: 'capitalize',
                boxShadow: tab === option ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              {option.replace('-', ' ')}
            </div>
          ))}
        </div>

        {/* 2. Body (Search & Filters) */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          padding: '0.8rem 1rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Búsqueda */}
          <div style={{
            flex: 1,
            minWidth: '200px',
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '999px',
            padding: '0.4rem 1rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
            <input
              type="text"
              placeholder="Buscar mis cursos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
            />
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: '0.8rem', flex: '0 0 auto', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <select
              value={selectedSkill}
              onChange={(e) => setSelectedSkill(e.target.value)}
              style={{
                borderRadius: '999px',
                border: '1px solid #e2e8f0',
                padding: '0.4rem 2rem 0.4rem 1rem',
                fontSize: '0.85rem',
                color: '#475569',
                outline: 'none',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '160px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <option value="">Habilidad</option>
              {tiposHabilidad.map(th => (
                <option key={th.idTipoHabilidad} value={th.idTipoHabilidad}>{th.nombre}</option>
              ))}
            </select>

            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              style={{
                borderRadius: '999px',
                border: '1px solid #e2e8f0',
                padding: '0.4rem 2rem 0.4rem 1rem',
                fontSize: '0.85rem',
                color: '#475569',
                outline: 'none',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '140px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <option value="">Nivel</option>
              {niveles.map(n => (
                <option key={n.idNivel} value={n.idNivel}>{n.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Grid de cursos estilo AdministrarCursos, solo lectura */}
      {loading ? (
        <div className="catalog-loading">Cargando cursos...</div>
      ) : (
        <div className="courses-grid fixed-cards">
          {filtrados.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "3rem",
                color: "#94a3b8",
              }}
            >
              No se encontraron cursos.
            </div>
          ) : (
            filtrados.map((curso) => {
              const hasImage = !!curso.imagenRuta;

              // Calcular visuales de estado para usarlos en toda la tarjeta
              const statusVisuals = (() => {
                // Detectar si está por terminar (tiene fecha límite y es hoy o a futuro)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const deadlineDate = curso.fechaTermino ? new Date(curso.fechaTermino) : null;
                if (deadlineDate) deadlineDate.setHours(0, 0, 0, 0);

                const isAboutToExpire = curso.estado === 'en-curso' && deadlineDate && today <= deadlineDate;
                const isExpired = curso.estado === 'en-curso' && deadlineDate && today > deadlineDate;

                if (isAboutToExpire) {
                  return { bg: '#d97706', icon: <FiClock />, warning: true };
                }

                if (isExpired) {
                  return { bg: '#991b1b', icon: <FiSlash />, expired: true };
                }

                switch (curso.estado) {
                  case "en-curso": return { bg: '#0369a1', icon: <FiClock /> };
                  case "finalizado":
                  case "aprobado": return { bg: '#15803d', icon: <FiCheckCircle /> };
                  case "suspendido":
                  case "reprobado": return { bg: '#991b1b', icon: <FiSlash /> };
                  default: return { bg: '#64748b', icon: <FiClock /> };
                }
              })();

              return (
                <article
                  key={curso.idEstudiantesCursos}
                  className="course-card fixed-card"
                  style={{
                    border: `1px solid ${statusVisuals.bg}`,
                    transition: 'all 0.3s ease',
                    boxShadow: statusVisuals.warning ? '0 10px 15px -3px rgba(217, 119, 6, 0.1)' : 'none'
                  }}
                >
                  <div className="course-thumb" style={{ padding: 0, position: 'relative' }}>
                    {hasImage ? (
                      <img
                        src={curso.imagenRuta?.startsWith('http://') || curso.imagenRuta?.startsWith('https://')
                          ? curso.imagenRuta
                          : `${SERVER_URL}${curso.imagenRuta}`}
                        alt={curso.nombre || "Imagen del curso"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "fill",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div className={`course-thumb course-thumb-1`} style={{ height: '100%' }} />
                    )}

                    {/* Triángulo de estatus en la esquina superior derecha */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '60px',
                      height: '60px',
                      zIndex: 5,
                      overflow: 'hidden'
                    }}>
                      {/* El Triángulo */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '0 60px 60px 0',
                        borderColor: `transparent ${statusVisuals.bg} transparent transparent`,
                        filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                      }} />

                      {/* El Icono */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        color: 'white',
                        fontSize: '1.5rem',
                        display: 'flex',
                        zIndex: 6
                      }}>
                        {statusVisuals.icon}
                      </div>
                    </div>
                  </div>
                  <div className="course-body" style={{
                    backgroundColor: (curso.estado === 'finalizado' || curso.estado === 'aprobado')
                      ? '#f0fdf4'
                      : (curso.estado === 'reprobado' || curso.estado === 'suspendido' || statusVisuals.expired)
                        ? '#fef2f2'
                        : statusVisuals.warning
                          ? '#fffbeb'
                          : '#ffffff',
                    flex: 1,
                    transition: 'background-color 0.3s ease'
                  }}>
                    <h3 className="course-title">{curso.nombre}</h3>
                    <div style={{ marginTop: "0.8rem" }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                        {/* Habilidad - Barra completa */}
                        <div style={{
                          fontSize: '0.8rem',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontWeight: 600,
                          width: '100%',
                          display: 'block',
                          textAlign: 'left'
                        }}>
                          {curso.tipoHabilidadNombre || "General"}
                        </div>

                        {/* Nivel - Barra completa */}
                        <div style={{
                          fontSize: '0.8rem',
                          backgroundColor: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontWeight: 600,
                          width: '100%',
                          display: 'block',
                          textAlign: 'left'
                        }}>
                          {curso.nivelNombre || "No especificado"}
                        </div>
                      </div>

                      {/* Info de Instructor y Duración (Texto simple) */}
                      <div style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '0.5rem' }}>
                        {/* Contenedor de ancho completo para igualar estilo con Habilidad/Nivel */}
                        <div style={{ minHeight: '28px', display: 'flex', alignItems: 'center', marginBottom: '0.6rem' }}>
                          {curso.fechaTermino ? (
                            (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const deadlineDate = new Date(curso.fechaTermino);
                              deadlineDate.setHours(0, 0, 0, 0);

                              const isExpired = today > deadlineDate;
                              const diffTime = deadlineDate.getTime() - today.getTime();
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                              return (
                                <div style={{
                                  fontSize: '0.8rem',
                                  backgroundColor: isExpired ? '#fee2e2' : '#fef3c7',
                                  color: isExpired ? '#991b1b' : '#92400e',
                                  padding: '4px 12px',
                                  borderRadius: '9999px',
                                  fontWeight: 600,
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  textAlign: 'left'
                                }}>
                                  <FiClock />
                                  {isExpired
                                    ? `Expiró el: ${deadlineDate.toLocaleDateString()}`
                                    : `Límite: ${deadlineDate.toLocaleDateString()} (${diffDays} días)`}
                                </div>
                              );
                            })()
                          ) : (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '0.8rem',
                              color: '#64748b',
                              fontWeight: 600,
                              paddingLeft: '12px',
                            }}>
                              <FiClock style={{ marginRight: '8px' }} />
                              {curso.duracion ? `${curso.duracion} días` : "Duración no especificada"}
                            </div>
                          )}
                        </div>

                        {curso.instructorNombre && (
                          <div style={{
                            marginTop: '0.8rem',
                            color: '#1e293b',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            textAlign: 'center'
                          }}>
                            Instructor: {curso.instructorNombre}
                          </div>
                        )}

                        <div style={{ marginTop: '0.8rem', display: 'flex', alignItems: 'center' }}>
                          {curso.resultado && curso.resultado !== "—" &&
                            curso.resultado.toLowerCase() !== curso.estado.replace('-', ' ').toLowerCase() &&
                            !(curso.resultado.toLowerCase() === 'aprobado' && curso.estado === 'finalizado') && (
                              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase' }}>
                                Resultado: {curso.resultado}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                    <div className="course-actions">
                      <button
                        type="button"
                        className="btn-action"
                        style={{
                          width: '100%',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          transition: '0.2s',
                          fontFamily: "'Outfit', sans-serif"
                        }}
                        disabled={!curso.idCurso}
                        onClick={() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dl = curso.fechaTermino ? new Date(curso.fechaTermino) : null;
                          if (dl) dl.setHours(0, 0, 0, 0);
                          const isExpired = dl && today > dl;

                          if (isExpired) {
                            handleSolicitarExtensionClick(curso);
                          } else {
                            handleOpenCurso(curso);
                          }
                        }}
                      >
                        {(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dl = curso.fechaTermino ? new Date(curso.fechaTermino) : null;
                          if (dl) dl.setHours(0, 0, 0, 0);
                          const isExpired = dl && today > dl;

                          if (!curso.idCurso) return "Sin contenido asignado";
                          if (isExpired) return "Solicitar extensión";
                          return "Ver curso";
                        })()}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      )}

      {/* Alerta general (SweetAlert fallback a tu propio Alert) */}
      <AlertDialog
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        buttonText={alertConfig.buttonText}
        cancelText={alertConfig.cancelText}
        onCancel={alertConfig.onCancel}
        onClose={alertConfig.onClose}
      />
    </div>
  );
};

export default MisCursos;
